// src/App.js
import {
  Routes,
  Route,
  Link,
  NavLink,
  Navigate,
  useLocation,
  useParams,
  useNavigate,
} from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

import { useTranslation } from "react-i18next";
import i18n from "i18next";
import Login from "./Login";
import Register from "./Register";
import AddProperty from "./AddProperty";
import Verify from "./Verify";
import PropertyDetails from "./PropertyDetails";
import MyProperties from "./MyProperties";
import Privacy from "./Privacy";
import Terms from "./Terms";

// ✅ Cities page (moved out)
import CitiesPage from "./CitiesPage";

// ✅ Admin
import AdminRoute from "./AdminRoute";
import AdminDashboard from "./admin/AdminDashboard";
import AdminProperties from "./admin/AdminProperties";
import AdminBookings from "./admin/AdminBookings";

/* ===================== API ===================== */
const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || "https://api.hellastay.com"
    : "http://localhost:8000";

/* ===================== Small helpers ===================== */
const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

function locationText(p) {
  if (!p) return "";
  if (typeof p.location === "string") return p.location;
  if (p.location?.text) return p.location.text;
  return "";
}

function startingPrice(p) {
  const firstRoom = (p?.rooms || [])[0];
  const rp = Number(firstRoom?.price || 0);
  if (rp > 0) return rp;
  return Number(p?.price || 0);
}

function clampText(s, n = 70) {
  const str = String(s || "").replace(/\s+/g, " ").trim();
  if (!str) return "";
  if (str.length <= n) return str;

  const cut = str.slice(0, n + 1);
  const lastSpace = cut.lastIndexOf(" ");
  const nice =
    lastSpace > Math.max(12, n - 20) ? cut.slice(0, lastSpace) : cut.slice(0, n);

  return nice.trim() + "…";
}

function ratingValue(p) {
  const r = Number(p?.rating || 0);
  if (!Number.isFinite(r) || r <= 0) return null;
  return Math.max(0, Math.min(5, r));
}

function reviewCountValue(p) {
  const c = Number(p?.reviewsCount || p?.reviewCount || 0);
  if (!Number.isFinite(c) || c <= 0) return 0;
  return Math.max(0, Math.floor(c));
}

function money(n) {
  const x = Number(n || 0);
  return x.toFixed(0);
}

function isNew(p) {
  const now = Date.now();
  const created = new Date(p?.createdAt || now).getTime();
  return created >= now - thirtyDaysMs;
}

function safeId(p, fallbackId) {
  const id = p?.id ?? p?._id ?? fallbackId;
  return id == null ? "" : String(id);
}

function normalizeImages(images) {
  const arr = Array.isArray(images) ? images : [];
  const out = [];

  for (const item of arr) {
    if (!item) continue;

    if (typeof item === "string") {
      out.push(item);
      continue;
    }

    if (
      typeof item === "object" &&
      typeof item.url === "string" &&
      item.url.trim()
    ) {
      out.push(item.url.trim());
      continue;
    }
  }

  return out.length > 0 ? out : [];
}

function normalizeProperty(raw) {
  if (!raw || typeof raw !== "object") return raw;

  const id = safeId(raw);
  const ownerId = raw.ownerId ?? raw.owner ?? raw.owner?._id;

  return {
    ...raw,
    id,
    _id: raw._id ?? raw.id,
    ownerId: ownerId != null ? String(ownerId) : raw.ownerId,
    _imagesRaw: raw.images,
    images: normalizeImages(raw.images),
    rating: Number(raw?.rating || 0),
    reviewsCount: Number(raw?.reviewsCount || raw?.reviewCount || 0),
  };
}

/* ===================== ✅ City helpers ===================== */
function normalizeSlug(s) {
  const str = String(s || "").trim().toLowerCase();
  if (!str) return "";

  const noDia = str.normalize
    ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    : str;

  return noDia
    .replace(/['’]/g, "")
    .replace(/[\s/_|]+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function bestCityName(p) {
  if (!p) return "";

  const cand =
    p.city ||
    p.town ||
    p.village ||
    p.municipality ||
    p.region ||
    p.location?.city ||
    p.location?.town ||
    p.location?.village ||
    p.location?.region;

  if (cand && String(cand).trim()) return String(cand).trim();

  const loc = locationText(p);
  if (!loc) return "";
  const first = String(loc).split(",")[0];
  return String(first || "").trim();
}

function titleCaseWords(s) {
  const clean = String(s || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "";
  return clean
    .split(" ")
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/* ===================== Icons (no emojis) ===================== */
function I({ children }) {
  return (
    <span
      className="chip-ico"
      aria-hidden="true"
      style={{ display: "inline-flex", alignItems: "center" }}
    >
      {children}
    </span>
  );
}
const icoStyle = { width: 16, height: 16, display: "block" };

const IconWifi = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M2 8.5C7.8 3.7 16.2 3.7 22 8.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M5 12c4.2-3.4 9.8-3.4 14 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M8.5 15.5c2.2-1.7 4.8-1.7 7 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M12 19.2h.01"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
    />
  </svg>
);

const IconParking = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M6.5 20V4h6a5 5 0 010 10h-6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.5 14h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IconPool = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M7 4v9M17 4v9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 13h14"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 18c1.5 0 1.5-1 3-1s1.5 1 3 1 1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IconSparkle = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M12 2l1.2 5.2L18 8.4l-4.8 1.2L12 15l-1.2-5.4L6 8.4l4.8-1.2L12 2z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M19.5 13.5l.6 2.4 2.4.6-2.4.6-.6 2.4-.6-2.4-2.4-.6 2.4-.6.6-2.4z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </svg>
);

const IconHeart = ({ filled = false }) => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M12 21s-7-4.6-9.3-8.7C.7 8.7 3 5.5 6.6 5.5c2 0 3.2 1.1 3.9 2 .7-.9 1.9-2 3.9-2 3.6 0 5.9 3.2 3.9 6.8C19 16.4 12 21 12 21z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const IconSliders = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M4 6h16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7 6v0"
      stroke="currentColor"
      strokeWidth="5"
      strokeLinecap="round"
    />
    <path
      d="M4 12h16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14 12v0"
      stroke="currentColor"
      strokeWidth="5"
      strokeLinecap="round"
    />
    <path
      d="M4 18h16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M10 18v0"
      stroke="currentColor"
      strokeWidth="5"
      strokeLinecap="round"
    />
  </svg>
);

const IconSnow = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M12 3v18M5 6l14 12M19 6L5 18"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const IconKitchen = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path d="M6 4h12v16H6z" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M9 8h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M9 12h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IconTV = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path d="M5 7h14v9H5z" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M9 20h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M10 16.5l-1 3.5M14 16.5l1 3.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const IconWasher = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path d="M6 4h12v16H6z" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M9 7h.01M12 7h.01M15 7h.01"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
    />
    <path
      d="M12 14a3 3 0 100-6 3 3 0 000 6z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const IconFlame = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M12 21c3 0 6-2.3 6-6 0-4-3-5.8-4-9-2 1-3 3-3 5-1-1-2-2-3-4-2 3-4 5-4 8 0 3.7 3 6 8 6z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const IconElevator = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path d="M7 3h10v18H7z" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M10 7l2-2 2 2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 17l-2 2-2-2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconWave = () => (
  <svg viewBox="0 0 24 24" fill="none" style={icoStyle}>
    <path
      d="M2 16c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IconUser = ({ filled = false, size = 18 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    style={{ width: size, height: size, display: "block" }}
    aria-hidden="true"
  >
    <path
      d="M12 12a4 4 0 100-8 4 4 0 000 8z"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M4.5 20.2c1.7-3 4.2-4.7 7.5-4.7s5.8 1.7 7.5 4.7"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ===================== Shared Favorites ===================== */
function useFavsStorage() {
  const [favs, setFavs] = useState(() => {
    try {
      const raw = localStorage.getItem("favs");
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("favs", JSON.stringify(Array.from(favs)));
    } catch {}
  }, [favs]);

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return { favs, toggleFav };
}

/* ===================== Card ===================== */
function PropertyCard({ p, fallbackId, isFav, onToggleFav }) {
  const loc = locationText(p) || "Unknown";
  const price = startingPrice(p);
  const r = ratingValue(p);
  const reviewsCount = reviewCountValue(p);

  const images =
    Array.isArray(p?.images) && p.images.length > 0
      ? p.images
      : ["/placeholder.png"];

  const [idx, setIdx] = useState(0);
  const hasCarousel = images.length > 1;

  const clampIdx = (n) => {
    const m = images.length;
    return ((n % m) + m) % m;
  };

  const prev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => clampIdx(i - 1));
  };

  const next = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => clampIdx(i + 1));
  };

  const goTo = (e, n) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx(clampIdx(n));
  };

  const id = safeId(p, fallbackId);

  return (
    <Link
      to={`/property/${id}`}
      className="property-link hs-card"
      aria-label={`Open ${p.title || "property"}`}
    >
      <div className="property-card pc-card--interactive">
        <div className="pc-media hs-media">
          <img
            src={images[idx]}
            alt={p.title || "Property"}
            className="pc-img hs-img"
            loading="lazy"
            decoding="async"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          <div className="pc-media-gradient" aria-hidden="true" />

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFav(id);
            }}
            className="pc-heart"
            title={isFav ? "Remove from favorites" : "Save"}
            aria-label={isFav ? "Remove from favorites" : "Save"}
          >
            <span className="pc-heart-ico" aria-hidden="true">
              <IconHeart filled={isFav} />
            </span>
          </button>

          {isNew(p) && <div className="pc-badge">✨ New</div>}

          {hasCarousel && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous photo"
                className="pc-arrow pc-arrow-left"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next photo"
                className="pc-arrow pc-arrow-right"
              >
                ›
              </button>
            </>
          )}

          {hasCarousel && (
            <div className="pc-dots" aria-hidden="true">
              {images.slice(0, 7).map((_, dotIdx) => {
                const active = dotIdx === idx;
                return (
                  <button
                    key={dotIdx}
                    type="button"
                    onClick={(e) => goTo(e, dotIdx)}
                    className={`pc-dot ${active ? "active" : ""}`}
                    title={`Photo ${dotIdx + 1}`}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="pc-body">
          <div className="pc-top">
            <div className="pc-left">
              <div className="pc-title" title={p.title || "Untitled"}>
                {p.title || "Untitled"}
              </div>

              <div className="pc-loc" title={loc}>
                {clampText(loc, 46)}
              </div>
            </div>

            <div className="pc-rating">
              {r ? (
                <span title={reviewsCount > 0 ? `${reviewsCount} reviews` : "Rating"}>
                  ★ {r.toFixed(1)}
                  {reviewsCount > 0 ? ` (${reviewsCount})` : ""}
                </span>
              ) : (
                <span className="pc-muted">New</span>
              )}
            </div>
          </div>

          {p.description && (
            <div className="pc-desc">{clampText(p.description, 42)}</div>
          )}

          <div className="pc-price">
            €{money(price)} <span className="pc-per">/ night</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ===================== Home ===================== */
function Home({ properties }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [chip, setChip] = useState("all");
  const [sort, setSort] = useState("recommended");
  const [view, setView] = useState("list");

  const [suggestOpen, setSuggestOpen] = useState(false);
  const searchWrapRef = useRef(null);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  const { favs, toggleFav } = useFavsStorage();

  useEffect(() => {
    const onDown = (e) => {
      if (moreOpen) {
        const el = moreRef.current;
        if (el && !el.contains(e.target)) setMoreOpen(false);
      }
      if (suggestOpen) {
        const sEl = searchWrapRef.current;
        if (sEl && !sEl.contains(e.target)) setSuggestOpen(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [moreOpen, suggestOpen]);

  const goSearch = () => {
    const s = q.trim();
    if (!s) {
      document
        .querySelector(".results-head")
        ?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const slug = normalizeSlug(s);
    navigate(`/search/${encodeURIComponent(slug || s)}`);
  };

  const hasAmenity = (p, key) => {
    if (!p) return false;
    if (typeof p[key] === "boolean") return !!p[key];
    if (typeof p?.amenities?.[key] === "boolean") return !!p.amenities[key];
    return !!p?.amenities?.[key] || !!p?.[key];
  };

  const MORE_FILTER_IDS = useMemo(
    () =>
      new Set(["ac", "kitchen", "tv", "washer", "heating", "elevator", "seaView"]),
    []
  );
  const moreCount = MORE_FILTER_IDS.has(chip) ? 1 : 0;

  const citySuggestions = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];

    const want = normalizeSlug(s);
    const map = new Map();

    for (const p of properties || []) {
      const name = bestCityName(p);
      const slug = normalizeSlug(name);
      if (!name || !slug) continue;

      const hit = name.toLowerCase().includes(s) || slug.includes(want);
      if (!hit) continue;

      const prev = map.get(slug);
      if (prev) map.set(slug, { name: prev.name, count: prev.count + 1 });
      else map.set(slug, { name, count: 1 });
    }

    const arr = Array.from(map.entries()).map(([slug, v]) => ({
      slug,
      name: v.name,
      count: v.count,
    }));

    arr.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return arr.slice(0, 8);
  }, [properties, q]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const max = maxPrice === "" ? null : Number(maxPrice);

    let list = (properties || []).filter((p) => {
      const loc = locationText(p).toLowerCase();
      const title = String(p?.title || "").toLowerCase();
      const price = startingPrice(p);
      const id = safeId(p);

      const matchesText = !query || loc.includes(query) || title.includes(query);
      const matchesPrice = max === null || price <= max;

      const matchesChip =
        chip === "all" ||
        (chip === "wifi" && hasAmenity(p, "wifi")) ||
        (chip === "parking" && hasAmenity(p, "parking")) ||
        (chip === "pool" && hasAmenity(p, "pool")) ||
        (chip === "new" && isNew(p)) ||
        (chip === "saved" && favs.has(id)) ||
        (chip === "ac" && hasAmenity(p, "ac")) ||
        (chip === "kitchen" && hasAmenity(p, "kitchen")) ||
        (chip === "tv" && hasAmenity(p, "tv")) ||
        (chip === "washer" && hasAmenity(p, "washer")) ||
        (chip === "heating" && hasAmenity(p, "heating")) ||
        (chip === "elevator" && hasAmenity(p, "elevator")) ||
        (chip === "seaView" && hasAmenity(p, "seaView"));

      return matchesText && matchesPrice && matchesChip;
    });

    if (sort === "priceAsc") {
      list = list.slice().sort((a, b) => startingPrice(a) - startingPrice(b));
    } else if (sort === "ratingDesc") {
      list = list
        .slice()
        .sort((a, b) => (ratingValue(b) || 0) - (ratingValue(a) || 0));
    } else if (sort === "newest") {
      list = list
        .slice()
        .sort(
          (a, b) =>
            new Date(b?.createdAt || 0).getTime() -
            new Date(a?.createdAt || 0).getTime()
        );
    }

    return list;
  }, [properties, q, maxPrice, chip, sort, favs]);

  const ChipBtn = ({ id, icon, label }) => (
    <button
      type="button"
      onClick={() => setChip(id)}
      className={`chip ${chip === id ? "active" : ""}`}
    >
      {icon ? <I>{icon}</I> : null}
      <span>{label}</span>
    </button>
  );

  const primaryChips = useMemo(
    () => [
      { id: "all", icon: null, label: t("home.chips.all", { defaultValue: "All" }) },
      { id: "wifi", icon: <IconWifi />, label: "Wi-Fi" },
      {
        id: "parking",
        icon: <IconParking />,
        label: t("home.chips.parking", { defaultValue: "Parking" }),
      },
      { id: "pool", icon: <IconPool />, label: t("home.chips.pool", { defaultValue: "Pool" }) },
      { id: "new", icon: <IconSparkle />, label: t("home.chips.new", { defaultValue: "New" }) },
      {
        id: "saved",
        icon: <IconHeart filled={chip === "saved"} />,
        label: t("home.chips.saved", { defaultValue: "Saved" }),
      },
    ],
    [t, chip]
  );

  const moreChips = useMemo(
    () => [
      { id: "ac", icon: <IconSnow />, label: t("home.chips.ac", { defaultValue: "AC" }) },
      {
        id: "kitchen",
        icon: <IconKitchen />,
        label: t("home.chips.kitchen", { defaultValue: "Kitchen" }),
      },
      { id: "tv", icon: <IconTV />, label: t("home.chips.tv", { defaultValue: "TV" }) },
      { id: "washer", icon: <IconWasher />, label: t("home.chips.washer", { defaultValue: "Washer" }) },
      {
        id: "heating",
        icon: <IconFlame />,
        label: t("home.chips.heating", { defaultValue: "Heating" }),
      },
      {
        id: "elevator",
        icon: <IconElevator />,
        label: t("home.chips.elevator", { defaultValue: "Elevator" }),
      },
      {
        id: "seaView",
        icon: <IconWave />,
        label: t("home.chips.seaView", { defaultValue: "Sea view" }),
      },
    ],
    [t]
  );

  return (
    <div className="home">
      <header className="hero home-hero">
        <div className="hero-overlay">
          <div className="hero-shell">
            <div className="hero-search-wrap" ref={searchWrapRef}>
              <div
                className="hero-search hero-search--two"
                style={{ gridTemplateColumns: "2fr 1fr auto", alignItems: "center" }}
              >
                <div style={{ position: "relative" }}>
                  <input
                    placeholder={t("home.cityPlaceholder")}
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setSuggestOpen(true);
                    }}
                    onFocus={() => setSuggestOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") goSearch();
                      if (e.key === "Escape") setSuggestOpen(false);
                    }}
                    aria-label="City"
                  />

                  {suggestOpen && citySuggestions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 8px)",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid rgba(0,0,0,0.12)",
                        borderRadius: 14,
                        boxShadow: "0 18px 55px rgba(0,0,0,0.14)",
                        overflow: "hidden",
                        zIndex: 100,
                      }}
                      role="listbox"
                      aria-label="Suggestions"
                    >
                      {citySuggestions.map((c) => (
                        <button
                          key={c.slug}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setQ(c.name);
                            setSuggestOpen(false);
                            navigate(`/city/${encodeURIComponent(c.slug)}`);
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "12px 14px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontWeight: 700,
                          }}
                        >
                          <span>{c.name}</span>
                          <span style={{ opacity: 0.6, fontWeight: 800 }}>{c.count}</span>
                        </button>
                      ))}

                      <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSuggestOpen(false);
                            goSearch();
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "10px 14px",
                            border: "none",
                            background: "rgba(0,0,0,0.02)",
                            cursor: "pointer",
                            fontWeight: 800,
                          }}
                        >
                          See results →
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <input
                  placeholder={t("home.maxPricePlaceholder")}
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  min={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") goSearch();
                  }}
                  aria-label="Max price"
                />

                <button
                  type="button"
                  className="search-btn"
                  onClick={goSearch}
                  aria-label="Search"
                >
                  <span className="search-ico" aria-hidden="true" />
                </button>
              </div>

              <div
                style={{
                  marginTop: 12,
                  fontSize: 13,
                  color: "rgba(34,34,34,0.62)",
                  fontWeight: 500,
                  textAlign: "center",
                  maxWidth: 620,
                  marginLeft: "auto",
                  marginRight: "auto",
                  lineHeight: 1.45,
                  letterSpacing: "-0.1px",
                }}
              >
                Travel with purpose. When hellastay becomes monetized, a portion of future profits will be donated to charitable causes.
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="filters-bar">
        <div className="page filters-inner">
          <Link
            to="/cities"
            className="select-pill"
            style={{ textDecoration: "none", marginRight: 10 }}
          >
            {t("home.cities", { defaultValue: "All cities" })}
          </Link>

          {primaryChips.map((c) => (
            <ChipBtn key={c.id} id={c.id} icon={c.icon} label={c.label} />
          ))}

          <div
            className="chip-more"
            ref={moreRef}
            style={{ position: "relative", display: "inline-flex" }}
          >
            <button
              type="button"
              className={`chip ${moreOpen ? "active" : ""}`}
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen ? "true" : "false"}
              title={t("home.chips.more", { defaultValue: "More filters" })}
            >
              <I>
                <IconSliders />
              </I>
              <span>{t("home.chips.more", { defaultValue: "More" })}</span>
              {moreCount > 0 ? (
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 8,
                    minWidth: 20,
                    height: 20,
                    borderRadius: 999,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    border: "1px solid rgba(0,0,0,0.12)",
                    background: "rgba(0,0,0,0.04)",
                  }}
                >
                  {moreCount}
                </span>
              ) : null}
            </button>

            {moreOpen && (
              <div
                className="chip-more-pop"
                role="dialog"
                aria-label={t("home.chips.more", { defaultValue: "More filters" })}
                style={{
                  position: "absolute",
                  top: "calc(100% + 10px)",
                  left: 0,
                  zIndex: 50,
                  minWidth: 280,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.10)",
                  borderRadius: 16,
                  boxShadow: "0 18px 55px rgba(0,0,0,0.14)",
                  padding: 12,
                  display: "grid",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontWeight: 850,
                    fontSize: 13,
                    padding: "2px 6px 6px",
                    color: "rgba(0,0,0,0.72)",
                  }}
                >
                  {t("home.chips.moreTitle", { defaultValue: "More filters" })}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                  }}
                >
                  {moreChips.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setChip(c.id);
                        setMoreOpen(false);
                      }}
                      className={`chip ${chip === c.id ? "active" : ""}`}
                      style={{ justifyContent: "flex-start" }}
                    >
                      <I>{c.icon}</I>
                      <span>{c.label}</span>
                    </button>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    justifyContent: "flex-end",
                    paddingTop: 6,
                  }}
                >
                  <button
                    type="button"
                    className="select-pill"
                    onClick={() => {
                      setChip("all");
                      setMoreOpen(false);
                    }}
                  >
                    {t("home.chips.clear", { defaultValue: "Clear" })}
                  </button>
                  <button
                    type="button"
                    className="select-pill"
                    onClick={() => setMoreOpen(false)}
                  >
                    {t("home.chips.done", { defaultValue: "Done" })}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="filters-spacer" />

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="select-pill"
            aria-label={t("home.sort.aria")}
          >
            <option value="recommended">{t("home.sort.recommended")}</option>
            <option value="priceAsc">{t("home.sort.priceAsc")}</option>
            <option value="ratingDesc">{t("home.sort.ratingDesc")}</option>
            <option value="newest">{t("home.sort.newest")}</option>
          </select>

          <div className="segmented">
            <button
              type="button"
              onClick={() => setView("list")}
              className={`seg-btn ${view === "list" ? "active" : ""}`}
            >
              {t("home.view.list")}
            </button>
            <button
              type="button"
              onClick={() => setView("map")}
              className={`seg-btn ${view === "map" ? "active" : ""}`}
            >
              {t("home.view.map")}
            </button>
          </div>
        </div>
      </div>

      <main className="page">
        <div className="results-head">
          <h2>{t("home.availableStays")}</h2>
          <div className="results-meta">
            {filtered.length}{" "}
            {t(filtered.length === 1 ? "home.resultOne" : "home.resultMany")}
          </div>
        </div>

        <div
          className="content-grid"
          style={{ gridTemplateColumns: view === "map" ? "2fr 1fr" : "1fr" }}
        >
          <div className="hs-grid hs-listings">
            {filtered.length === 0 ? (
              <div className="empty-card">
                <div className="empty-title">{t("home.empty.title")}</div>
                <div className="empty-sub">{t("home.empty.sub")}</div>
              </div>
            ) : (
              filtered.map((p, idx) => {
                const id = safeId(p, `property-${idx}`);
                return (
                  <PropertyCard
                    key={id}
                    p={p}
                    fallbackId={id}
                    isFav={favs.has(id)}
                    onToggleFav={toggleFav}
                  />
                );
              })
            )}
          </div>

          {view === "map" && (
            <div className="map-box">
              <div className="map-head">{t("home.map.title")}</div>
              <div className="map-sub">{t("home.map.sub")}</div>
              <div className="map-body" />
            </div>
          )}
        </div>

        <section
          aria-label="About hellastay"
          style={{
            marginTop: 58,
            paddingTop: 28,
            borderTop: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              maxWidth: 980,
              marginLeft: "auto",
              marginRight: "auto",
              display: "grid",
              gap: 18,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <h2 style={{ margin: 0, fontSize: 26, letterSpacing: -0.2 }}>
                Authentic stays in Greece — book with confidence on hellastay
              </h2>
              <p
                style={{
                  marginTop: 10,
                  marginBottom: 0,
                  color: "rgba(0,0,0,0.62)",
                  fontWeight: 600,
                  lineHeight: 1.6,
                }}
              >
                Discover apartments, villas, family guesthouses and boutique stays across Greece. Browse by city,
                compare amenities, save favorites, and choose the place that fits your trip.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <div
                style={{
                  background: "rgba(0,0,0,0.02)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 16,
                  padding: 14,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>
                  Find the right stay, faster
                </h3>
                <p style={{ marginTop: 8, marginBottom: 0, color: "rgba(0,0,0,0.62)", lineHeight: 1.55 }}>
                  Filter by Wi-Fi, parking, pool, sea view, kitchen and more. Sort by price, rating or newest listings.
                </p>
              </div>

              <div
                style={{
                  background: "rgba(0,0,0,0.02)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 16,
                  padding: 14,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>
                  Real places, clear details
                </h3>
                <p style={{ marginTop: 8, marginBottom: 0, color: "rgba(0,0,0,0.62)", lineHeight: 1.55 }}>
                  Each listing includes photos, location context and key amenities, so you can book the stay that matches your plans.
                </p>
              </div>

              <div
                style={{
                  background: "rgba(0,0,0,0.02)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 16,
                  padding: 14,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900 }}>
                  Travel with purpose
                </h3>
                <p style={{ marginTop: 8, marginBottom: 0, color: "rgba(0,0,0,0.62)", lineHeight: 1.55 }}>
                  When hellastay becomes monetized, a portion of future profits will be donated to charitable causes that support communities.
                </p>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 0.8fr",
                gap: 14,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 16,
                  padding: 14,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 950 }}>
                  Explore destinations in Greece
                </h3>
                <p style={{ marginTop: 8, marginBottom: 12, color: "rgba(0,0,0,0.62)", lineHeight: 1.55 }}>
                  Start with popular destinations, or open the full directory and browse stays by city.
                </p>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <Link to="/cities" className="select-pill" style={{ textDecoration: "none" }}>
                    Browse all cities
                  </Link>

                  <Link to="/search/athens" className="select-pill" style={{ textDecoration: "none" }}>
                    Athens stays
                  </Link>
                  <Link to="/search/santorini" className="select-pill" style={{ textDecoration: "none" }}>
                    Santorini stays
                  </Link>
                  <Link to="/search/crete" className="select-pill" style={{ textDecoration: "none" }}>
                    Crete stays
                  </Link>
                  <Link to="/search/mykonos" className="select-pill" style={{ textDecoration: "none" }}>
                    Mykonos stays
                  </Link>
                </div>
              </div>

              <div
                style={{
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 16,
                  padding: 14,
                  background: "rgba(0,0,0,0.015)",
                }}
              >
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 950 }}>
                  Helpful links
                </h3>
                <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                  <Link to="/terms" className="select-pill" style={{ textDecoration: "none", textAlign: "center" }}>
                    Terms
                  </Link>
                  <Link to="/privacy" className="select-pill" style={{ textDecoration: "none", textAlign: "center" }}>
                    Privacy
                  </Link>
                  <Link to="/cities" className="select-pill" style={{ textDecoration: "none", textAlign: "center" }}>
                    Cities directory
                  </Link>
                </div>
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                color: "rgba(0,0,0,0.56)",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.55,
                paddingTop: 4,
              }}
            >
              Tip: Search by city name, then refine results with filters like Wi-Fi, parking, pool and sea view.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ===================== City Page ===================== */
function CityPage({ properties }) {
  const { slug } = useParams();
  const { t } = useTranslation();

  const citySlug = useMemo(
    () => normalizeSlug(decodeURIComponent(String(slug || ""))),
    [slug]
  );

  const cityName = useMemo(() => {
    const raw = String(slug || "");
    const decoded = decodeURIComponent(raw);
    const nice = titleCaseWords(decoded);
    return nice || t("city.titleFallback", { defaultValue: "Location" });
  }, [slug, t]);

  useEffect(() => {
    document.title = `${cityName} – hellastay`;
  }, [cityName]);

  const { favs, toggleFav } = useFavsStorage();

  const list = useMemo(() => {
    const items = Array.isArray(properties) ? properties : [];
    if (!citySlug) return items;

    return items.filter((p) => {
      const cn = bestCityName(p);
      const s1 = normalizeSlug(cn);
      if (s1 && s1 === citySlug) return true;

      const loc = locationText(p);
      const s2 = normalizeSlug(loc);
      if (
        s2 &&
        (s2 === citySlug ||
          s2.startsWith(citySlug + "-") ||
          s2.includes("-" + citySlug + "-") ||
          s2.endsWith("-" + citySlug))
      )
        return true;

      return false;
    });
  }, [properties, citySlug]);

  return (
    <div className="page" style={{ paddingTop: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 28, letterSpacing: -0.2 }}>
            {t("city.h1", { defaultValue: "Stays in" })} {cityName}
          </h1>
          <div
            style={{
              marginTop: 6,
              color: "rgba(0,0,0,0.62)",
              fontWeight: 600,
            }}
          >
            {list.length}{" "}
            {t(list.length === 1 ? "home.resultOne" : "home.resultMany")}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link to="/cities" className="select-pill" style={{ textDecoration: "none" }}>
            {t("city.allCities", { defaultValue: "All cities" })}
          </Link>
          <Link to="/" className="select-pill" style={{ textDecoration: "none" }}>
            ← {t("city.back", { defaultValue: "Back to all" })}
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="hs-grid hs-listings">
          {list.length === 0 ? (
            <div className="empty-card">
              <div className="empty-title">
                {t("city.empty.title", { defaultValue: "No stays yet" })}
              </div>
              <div className="empty-sub">
                {t("city.empty.sub", {
                  defaultValue: "Try another location or check back soon.",
                })}
              </div>
            </div>
          ) : (
            list.map((p, idx) => {
              const id = safeId(p, `property-${idx}`);
              return (
                <PropertyCard
                  key={id}
                  p={p}
                  fallbackId={id}
                  isFav={favs.has(id)}
                  onToggleFav={toggleFav}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== Search Page ===================== */
function SearchPage({ properties }) {
  const { q } = useParams();
  const { t } = useTranslation();

  const raw = useMemo(() => decodeURIComponent(String(q || "")), [q]);
  const queryText = useMemo(() => titleCaseWords(raw), [raw]);
  const querySlug = useMemo(() => normalizeSlug(raw), [raw]);

  useEffect(() => {
    document.title = `${queryText || "Search"} – hellastay`;
  }, [queryText]);

  const { favs, toggleFav } = useFavsStorage();
  const [maxPrice, setMaxPrice] = useState("");

  const list = useMemo(() => {
    const items = Array.isArray(properties) ? properties : [];
    const max = maxPrice === "" ? null : Number(maxPrice);

    return items.filter((p) => {
      const loc = locationText(p).toLowerCase();
      const title = String(p?.title || "").toLowerCase();
      const city = bestCityName(p).toLowerCase();
      const price = startingPrice(p);

      const hit =
        !querySlug ||
        loc.includes(querySlug) ||
        title.includes(querySlug) ||
        city.includes(querySlug) ||
        normalizeSlug(loc).includes(querySlug) ||
        normalizeSlug(city).includes(querySlug);

      const okPrice = max === null || price <= max;
      return hit && okPrice;
    });
  }, [properties, querySlug, maxPrice]);

  return (
    <div className="page" style={{ paddingTop: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 28, letterSpacing: -0.2 }}>
            {t("search.h1", { defaultValue: "Search results for" })} “
            {queryText || raw || ""}”
          </h1>
          <div style={{ marginTop: 6, color: "rgba(0,0,0,0.62)", fontWeight: 600 }}>
            {list.length}{" "}
            {t(list.length === 1 ? "home.resultOne" : "home.resultMany")}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            className="select-pill"
            style={{ width: 160 }}
            placeholder={t("home.maxPricePlaceholder")}
            type="number"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            min={0}
          />
          <Link to="/cities" className="select-pill" style={{ textDecoration: "none" }}>
            {t("search.cities", { defaultValue: "All cities" })}
          </Link>
          <Link to="/" className="select-pill" style={{ textDecoration: "none" }}>
            ← {t("search.back", { defaultValue: "Back" })}
          </Link>
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="hs-grid hs-listings">
          {list.length === 0 ? (
            <div className="empty-card">
              <div className="empty-title">
                {t("search.empty.title", { defaultValue: "No stays found" })}
              </div>
              <div className="empty-sub">
                {t("search.empty.sub", { defaultValue: "Try another search." })}
              </div>
            </div>
          ) : (
            list.map((p, idx) => {
              const id = safeId(p, `property-${idx}`);
              return (
                <PropertyCard
                  key={id}
                  p={p}
                  fallbackId={id}
                  isFav={favs.has(id)}
                  onToggleFav={toggleFav}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================== Navbar ===================== */
function AirbnbNav({ user, setUser, compactForAddProperty = false }) {
  const { t } = useTranslation();

  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  const menuWrapRef = useRef(null);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const onDown = (e) => {
      const el = menuWrapRef.current;
      if (el && !el.contains(e.target)) setMenuOpen(false);
    };

    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {}
    setUser(null);
    setMenuOpen(false);
  };

  const isHost = user?.role === "host";
  const isCompactMobile = compactForAddProperty && isMobile;

  const LangButtons = ({ className = "" }) => (
    <div className={`lang-wrap ${className}`.trim()}>
      <button
        className={`lang-btn ${i18n.language === "en" ? "active" : ""}`}
        onClick={() => i18n.changeLanguage("en")}
        type="button"
      >
        EN
      </button>
      <button
        className={`lang-btn ${i18n.language === "el" ? "active" : ""}`}
        onClick={() => i18n.changeLanguage("el")}
        type="button"
      >
        ΕΛ
      </button>
    </div>
  );

  const MenuItem = ({ to, children, onClick }) => (
    <NavLink
      to={to}
      onClick={(e) => {
        if (onClick) onClick(e);
        setMenuOpen(false);
      }}
      style={{
        textDecoration: "none",
        color: "inherit",
        padding: "10px 12px",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontWeight: 800,
      }}
    >
      {children}
    </NavLink>
  );

  const UserBadge = ({ compact = false }) => (
    <span
      aria-hidden="true"
      style={{
        width: compact ? 22 : 30,
        height: compact ? 22 : 30,
        minWidth: compact ? 22 : 30,
        minHeight: compact ? 22 : 30,
        borderRadius: 999,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: user
          ? "linear-gradient(180deg, rgba(255,56,92,0.14), rgba(255,56,92,0.08))"
          : "rgba(17,24,39,0.05)",
        border: user
          ? "1px solid rgba(255,56,92,0.18)"
          : "1px solid rgba(34,34,34,0.10)",
        color: user ? "var(--brandDark)" : "rgba(34,34,34,0.68)",
        flex: "0 0 auto",
        lineHeight: 0,
      }}
    >
      <IconUser filled={!!user} size={compact ? 13 : 16} />
    </span>
  );

  if (isCompactMobile) {
    return (
      <div
        ref={menuWrapRef}
        style={{
          position: "fixed",
          top: 12,
          right: 12,
          zIndex: 1200,
        }}
      >
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-haspopup="menu"
          aria-expanded={menuOpen ? "true" : "false"}
          title={menuOpen ? "Close menu" : "Open menu"}
          style={{
            minWidth: 54,
            height: 48,
            borderRadius: 999,
            background: "#fff",
            border: "1px solid rgba(34,34,34,0.10)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
            fontWeight: 900,
            cursor: "pointer",
            color: user ? "var(--brandDark)" : "var(--text)",
            padding: "0 10px",
          }}
        >
          {menuOpen ? (
            "×"
          ) : (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                lineHeight: 1,
              }}
            >
              <UserBadge compact />
              <span
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                ☰
              </span>
            </span>
          )}
        </button>

        {menuOpen && (
          <div
            role="menu"
            aria-label="Menu"
            style={{
              position: "absolute",
              top: "calc(100% + 10px)",
              right: 0,
              width: minWidthForCompactMenu(user),
              maxWidth: "calc(100vw - 24px)",
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 20,
              boxShadow: "0 24px 60px rgba(0,0,0,0.16)",
              padding: 12,
              zIndex: 1300,
              display: "grid",
              gap: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "6px 6px 10px",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="nav-logo"
                  style={{
                    fontWeight: 800,
                    letterSpacing: -0.2,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    textDecoration: "none",
                    color: "inherit",
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(180deg, rgba(255,56,92,0.16), rgba(255,56,92,0.10))",
                      border: "1px solid rgba(255,56,92,0.22)",
                      color: "var(--brandDark)",
                      fontWeight: 800,
                      flex: "0 0 auto",
                    }}
                    aria-hidden="true"
                  >
                    HS
                  </span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "baseline",
                      gap: 6,
                      minWidth: 0,
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap" }}>hellastay</span>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: "var(--brand)",
                        display: "inline-block",
                      }}
                    />
                  </span>
                </Link>

                <UserBadge />
              </div>

              <LangButtons />
            </div>

            <MenuItem to="/cities">{t("nav.cities", { defaultValue: "Cities" })}</MenuItem>
            {user && <MenuItem to="/my-properties">{t("nav.myProperties")}</MenuItem>}
            {user && isHost && <MenuItem to="/add-property">{t("nav.addProperty")}</MenuItem>}
            {user?.role === "admin" && <MenuItem to="/admin">Admin</MenuItem>}
            <MenuItem to="/terms">{t("nav.terms", { defaultValue: "Terms" })}</MenuItem>
            <MenuItem to="/privacy">{t("nav.privacy", { defaultValue: "Privacy" })}</MenuItem>

            {!user ? (
              <>
                <div style={{ height: 1, background: "rgba(0,0,0,0.06)", margin: "4px 0" }} />
                <MenuItem to="/login">{t("nav.login")}</MenuItem>
                <MenuItem to="/register">{t("nav.register")}</MenuItem>
              </>
            ) : (
              <>
                <div style={{ height: 1, background: "rgba(0,0,0,0.06)", margin: "4px 0" }} />
                <button
                  type="button"
                  onClick={logout}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 12px",
                    borderRadius: 14,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "rgba(0,0,0,0.02)",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  {t("nav.logout")}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <Link
          to="/"
          className="nav-logo"
          style={{
            fontWeight: 700,
            letterSpacing: -0.2,
            display: "flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 999,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(180deg, rgba(255,56,92,0.16), rgba(255,56,92,0.10))",
              border: "1px solid rgba(255,56,92,0.22)",
              color: "var(--brandDark)",
              fontWeight: 800,
            }}
            aria-hidden="true"
          >
            HS
          </span>

          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            <span>hellastay</span>
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: "var(--brand)",
                display: "inline-block",
              }}
            />
          </span>
        </Link>

        <LangButtons className="lang-mobile" />
      </div>

      <div className="nav-menu" ref={menuWrapRef} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen ? "true" : "false"}
          style={{
            minWidth: 58,
            height: 42,
            borderRadius: 21,
            background: "#fff",
            border: "1px solid rgba(34,34,34,0.10)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
            fontWeight: 900,
            cursor: "pointer",
            color: user ? "var(--brandDark)" : "var(--text)",
            padding: "0 12px",
            transform: "translateY(1px)",
          }}
          title="Menu"
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              lineHeight: 1,
            }}
          >
            <UserBadge compact />
            <span
              style={{
                fontSize: 14,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              ☰
            </span>
          </span>
        </button>

        {menuOpen && (
          <div
            role="menu"
            aria-label="Menu"
            style={{
              position: "absolute",
              top: "calc(100% + 10px)",
              right: 0,
              width: 240,
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 16,
              boxShadow: "0 18px 55px rgba(0,0,0,0.14)",
              padding: 10,
              zIndex: 200,
              display: "grid",
              gap: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "4px 4px 10px",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                marginBottom: 2,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ fontWeight: 900, fontSize: 13, whiteSpace: "nowrap" }}>
                  {user ? "Signed in" : "Guest"}
                </span>
                <UserBadge />
              </div>

              {!user ? (
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  style={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: "var(--brandDark)",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("nav.login")}
                </Link>
              ) : null}
            </div>

            <MenuItem to="/cities">{t("nav.cities", { defaultValue: "Cities" })}</MenuItem>

            <MenuItem to="/terms">{t("nav.terms", { defaultValue: "Terms" })}</MenuItem>
            <MenuItem to="/privacy">{t("nav.privacy", { defaultValue: "Privacy" })}</MenuItem>

            {user && <MenuItem to="/my-properties">{t("nav.myProperties")}</MenuItem>}
            {user && isHost && <MenuItem to="/add-property">{t("nav.addProperty")}</MenuItem>}
            {user?.role === "admin" && <MenuItem to="/admin">Admin</MenuItem>}

            <div style={{ height: 1, background: "rgba(0,0,0,0.06)", margin: "6px 0" }} />

            {!user ? (
              <>
                <MenuItem to="/login">{t("nav.login")}</MenuItem>
                <MenuItem to="/register">{t("nav.register")}</MenuItem>
              </>
            ) : (
              <button
                type="button"
                onClick={logout}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.08)",
                  background: "rgba(0,0,0,0.02)",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                {t("nav.logout")}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="nav-links">
        {user && user?.role === "host" && (
          <NavLink to="/add-property">{t("nav.addProperty")}</NavLink>
        )}

        {!user ? (
          <>
            <NavLink to="/login">{t("nav.login")}</NavLink>
            <NavLink to="/register">{t("nav.register")}</NavLink>
          </>
        ) : (
          <>
            <NavLink to="/my-properties">{t("nav.myProperties")}</NavLink>
            <button onClick={logout} type="button">
              {t("nav.logout")}
            </button>
          </>
        )}

        {user?.role === "admin" && <NavLink to="/admin">Admin</NavLink>}

        <LangButtons className="lang-desktop" />
      </div>
    </nav>
  );
}

function minWidthForCompactMenu(user) {
  if (user?.role === "admin") return 272;
  return 252;
}

/* ===================== Meta Pixel ===================== */
function MetaPixelPageView() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.fbq === "function") window.fbq("track", "PageView");
  }, [location.pathname, location.search]);

  return null;
}

/* ===================== App ===================== */
function App() {
  const { t } = useTranslation();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAddPropertyRoute = location.pathname === "/add-property";

  useEffect(() => {
    const loadMe = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
        });
        if (!res.ok) {
          setUser(null);
          return;
        }
        const data = await res.json();
        setUser(data?.user ?? data ?? null);
      } catch {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };
    loadMe();
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/properties`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        const now = Date.now();
        const filtered = (Array.isArray(data) ? data : [])
          .map(normalizeProperty)
          .filter((p) => {
            const created = new Date(p?.createdAt || now).getTime();
            if (!Number.isFinite(created)) return true;
            return created >= now - thirtyDaysMs;
          });
        setProperties(filtered);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const addProperty = (p) => {
    const item = normalizeProperty(p);
    const id = safeId(item);

    setProperties((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const idx = list.findIndex((x) => safeId(x) === id);
      if (idx >= 0) {
        const copy = list.slice();
        copy[idx] = { ...copy[idx], ...item };
        return copy;
      }
      return [...list, item];
    });
  };

  const deleteProperty = async (id) => {
    const pid = String(id || "");
    if (!pid) return { success: false, message: "Missing id" };

    try {
      const res = await fetch(
        `${API_URL}/api/properties/${encodeURIComponent(pid)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      let data = {};
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) data = await res.json();
      else data = { message: await res.text() };

      if (!res.ok)
        return {
          success: false,
          message: data?.message || `Delete failed (${res.status})`,
        };

      setProperties((prev) => (prev || []).filter((p) => safeId(p) !== pid));
      return { success: true };
    } catch (e) {
      console.error(e);
      return { success: false, message: "Network error deleting property" };
    }
  };

  const isHost = user?.role === "host";

  const LoadingShell = () => (
    <div className="page">
      <div style={{ display: "grid", gap: 14 }}>
        <div className="skeleton-line" />
        <div className="skeleton-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-media" />
              <div className="skeleton-body">
                <div className="skeleton-row w70" />
                <div className="skeleton-row w45" />
                <div className="skeleton-row w55" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <MetaPixelPageView />

      <AirbnbNav
        user={user}
        setUser={setUser}
        compactForAddProperty={isAddPropertyRoute}
      />

      <Routes>
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        <Route
          path="/admin"
          element={
            <AdminRoute user={user}>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/properties"
          element={
            <AdminRoute user={user}>
              <AdminProperties />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <AdminRoute user={user}>
              <AdminBookings />
            </AdminRoute>
          }
        />

        <Route
          path="/cities"
          element={
            loading ? (
              <LoadingShell />
            ) : (
              <CitiesPage
                properties={properties}
                bestCityName={bestCityName}
                normalizeSlug={normalizeSlug}
                titleCaseWords={titleCaseWords}
              />
            )
          }
        />
        <Route
          path="/city/:slug"
          element={loading ? <LoadingShell /> : <CityPage properties={properties} />}
        />
        <Route
          path="/search/:q"
          element={loading ? <LoadingShell /> : <SearchPage properties={properties} />}
        />

        <Route path="/" element={loading ? <LoadingShell /> : <Home properties={properties} />} />

        <Route path="/login" element={<Login setUser={setUser} />} />
        <Route path="/register" element={<Register setUser={setUser} />} />

        <Route
          path="/add-property"
          element={
            authLoading ? null : user ? (
              isHost ? (
                <AddProperty addProperty={addProperty} user={user} />
              ) : (
                <Navigate to="/my-properties" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/my-properties"
          element={
            authLoading ? null : user ? (
              <MyProperties
                user={user}
                setUser={setUser}
                properties={properties}
                setProperties={setProperties}
                deleteProperty={deleteProperty}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/property/:id"
          element={
            <PropertyDetails
              user={user}
              properties={properties}
              setProperties={setProperties}
            />
          }
        />

        <Route path="/verify" element={<Verify />} />
      </Routes>

      <footer>
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            gap: 12,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <a href="/terms">{t("footer.terms", { defaultValue: "Terms" })}</a>
          <a href="/privacy">{t("footer.privacy", { defaultValue: "Privacy" })}</a>
        </div>

        <div
          style={{
            fontSize: 13,
            color: "var(--muted)",
            marginBottom: 6,
            fontWeight: 500,
          }}
        >
          Travel with purpose — when hellastay becomes monetized, a portion of future profits
          will support charitable causes.
        </div>

        <div>© 2026 hellastay</div>
      </footer>
    </>
  );
}

export default App;